import { IconButton, IconButtonWithText } from '..';
import { useTranslation } from '../../../hooks/useTranslation';
import { useState, useCallback, useEffect, useRef } from 'react';
import { TermDto, PostTermsDto } from '../../../commonTypes/client.types';
import useHttpPostPaymentTerms from '../../../hooks/client/clientApi/useHttpPostPaymentTerms';
import useHttpPatchPaymentTerm from '../../../hooks/client/clientApi/useHttpPatchPaymentTerm';
import useHttpDeletePaymentTerm from '../../../hooks/client/clientApi/useHttpDeletePaymentTerm';
import { ToastStateHandler } from '../../../api';
import { AppError, getUserMessage } from '../../../api/utils/ErrorHandler';
import { useContainer } from '../../../hooks/useContainer';

interface TermModalProps {
    onSave: (saveCallback: () => Promise<void>) => void;
    terms: Partial<TermDto>[];
    refetchTerms: () => void;
}

const getProtectedTermIds = (terms: Partial<TermDto>[]) =>
    terms
        .slice(0, 4)
        .map(term => term.id)
        .filter((id): id is string => Boolean(id));

export const InvoiceTermModal = ({ onSave, terms: initialTerms, refetchTerms }: TermModalProps) => {
    const { t } = useTranslation('invoice');
    const { t: tNotifications } = useTranslation('newNotifications');
    const { execPost } = useHttpPostPaymentTerms();
    const { execPatch } = useHttpPatchPaymentTerm();
    const { execDelete } = useHttpDeletePaymentTerm();
    const toastHandler = useContainer(ToastStateHandler);

    const [terms, setTerms] = useState<Partial<TermDto>[]>(initialTerms);
    const latestTermsRef = useRef<Partial<TermDto>[]>(initialTerms);
    const deletedTermIdsRef = useRef<string[]>([]);
    const protectedTermIds = getProtectedTermIds(initialTerms);

    useEffect(() => {
        setTerms(initialTerms);
        latestTermsRef.current = initialTerms;
        deletedTermIdsRef.current = [];
    }, [initialTerms]);

    const handleAddNew = () => {
        setTerms([...terms, { name: '', numberOfDays: 0 }]);
    };

    const handleChange = (index: number, field: keyof TermDto, value: string | number) => {
        const term = terms[index];
        if (term?.id && protectedTermIds.includes(term.id)) return;

        const updated = [...terms];
        if (field === 'numberOfDays') {
            updated[index].numberOfDays = Number(value);
        } else if (field === 'name') {
            updated[index].name = String(value);
        }
        setTerms(updated);
    };

    const handleDelete = (index: number) => {
        const term = terms[index];
        if (!term) return;
        if (term.id && protectedTermIds.includes(term.id)) return;

        if (!term.id) {
            const updated = terms.filter((_, i) => i !== index);
            setTerms(updated);
            return;
        }

        deletedTermIdsRef.current = [...deletedTermIdsRef.current, term.id];
        setTerms(prevTerms => prevTerms.filter(prevTerm => prevTerm.id !== term.id));
    };

    const patchTermAsync = useCallback(
        (id: string, body: PostTermsDto) =>
            new Promise<void>((resolve, reject) => {
                execPatch(id, body, {
                    onSuccess: resolve,
                    onError: reject,
                });
            }),
        [execPatch]
    );

    const postTermsAsync = useCallback(
        (newTerms: PostTermsDto[]) =>
            new Promise<void>((resolve, reject) => {
                execPost(newTerms, {
                    onSuccess: resolve,
                    onError: reject,
                });
            }),
        [execPost]
    );

    const deleteTermAsync = useCallback(
        (id: string) =>
            new Promise<void>((resolve, reject) => {
                execDelete(id, {
                    onSuccess: resolve,
                    onError: reject,
                });
            }),
        [execDelete]
    );

    useEffect(() => {
        latestTermsRef.current = terms;
    }, [terms]);
    const handleSave = useCallback(async () => {
        const currentTerms = latestTermsRef.current;
        const deletedTermIds = deletedTermIdsRef.current;
        const newTerms: PostTermsDto[] = currentTerms
            .filter(term => !term.id && term.name && term.numberOfDays !== undefined)
            .map(term => ({
                name: term.name!,
                numberOfDays: term.numberOfDays!,
            }));

        const termsForUpdate = currentTerms
            .filter(term => term.id && !protectedTermIds.includes(term.id) && term.name && term.numberOfDays !== undefined)
            .map(term => ({
                id: term.id!,
                body: {
                    name: term.name!,
                    numberOfDays: term.numberOfDays!,
                },
            }));

        if (newTerms.length === 0 && termsForUpdate.length === 0 && deletedTermIds.length === 0) {
            refetchTerms();
            return;
        }

        try {
            await Promise.all([
                ...deletedTermIds.map(id => deleteTermAsync(id)),
                ...termsForUpdate.map(term => patchTermAsync(term.id, term.body)),
                ...(newTerms.length > 0 ? [postTermsAsync(newTerms)] : []),
            ]);

            deletedTermIdsRef.current = [];
            toastHandler.push('success', tNotifications('invoice.termsUpdateTitle'), tNotifications('invoice.termsUpdateSuccess'));
            refetchTerms();
        } catch (error) {
            const message = getUserMessage(error as AppError | string, tNotifications('invoice.termsUpdateError'));
            toastHandler.push('error', tNotifications('invoice.termsUpdateTitle'), message);
            throw error;
        }
    }, [deleteTermAsync, patchTermAsync, postTermsAsync, protectedTermIds, refetchTerms, tNotifications, toastHandler]);

    useEffect(() => {
        onSave(handleSave);
    }, [onSave, handleSave]);

    return (
        <div className="invoice-term">
            <div className="invoice-term__content-container">
                <div className="invoice-create__table-container">
                    <table className="invoice-create__table">
                        <thead>
                            <tr className="invoice-create__table-row">
                                <th className="invoice-create__table-head-cell">
                                    <div className="invoice-create__header-cell">
                                        <span className="invoice-create__bold-text">{t('termName')}</span>
                                    </div>
                                </th>
                                <th className="invoice-create__table-head-cell">
                                    <div className="invoice-create__header-cell">
                                        <span className="invoice-create__bold-text">{t('termDay')}</span>
                                    </div>
                                </th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            {terms.map((term, index) => (
                                <tr key={term.id || index} className="invoice-create__table-row">
                                    <td className="invoice-create__table-cell">
                                        <input
                                            type="text"
                                            value={term.name ?? ''}
                                            placeholder="Enter Term Name"
                                            onChange={e => handleChange(index, 'name', e.target.value)}
                                            className="invoice-create__currency-select paragraph-3"
                                            disabled={!!term.id && protectedTermIds.includes(term.id)}
                                        />
                                    </td>
                                    <td className="invoice-create__table-cell">
                                        <input
                                            type="number"
                                            value={term.numberOfDays ?? 0}
                                            placeholder="0"
                                            onChange={e => handleChange(index, 'numberOfDays', e.target.value)}
                                            className="invoice-create__currency-select paragraph-3"
                                            disabled={!!term.id && protectedTermIds.includes(term.id)}
                                        />
                                    </td>
                                    <td className="invoice-create__table-cell-actions">
                                        {(!term.id || !protectedTermIds.includes(term.id)) && (
                                            <div className="invoice-create__table-cell-action">
                                                <IconButton
                                                    type="button"
                                                    iconUrl="/svg/close-x-red.svg"
                                                    variant="icon"
                                                    iconAlt="delete"
                                                    onClick={() => handleDelete(index)}
                                                />
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div>
                    <IconButtonWithText
                        onClick={handleAddNew}
                        iconSrc="/svg/plus-blue-without-circle.svg"
                        iconAlt="add"
                        variant="text"
                        textColour="blue"
                        text={t('addNewTerm')}
                    />
                </div>
            </div>
        </div>
    );
};
